import { IvrMenuNode, IvrMenuOption } from "./queue.model";

// Funções puras de navegação/validação sobre a árvore de menu do IVR
// (IvrMenuNode). Não fazem I/O — quem chama decide onde persistir.
// Contrato completo em docs/ura-multinivel-contrato.md.

// Nó novo, ainda sem opções — usado ao criar o primeiro submenu de uma
// opção, ou como raiz da árvore na primeira vez que uma empresa usa o
// menu multinível. message/error ficam undefined até serem editados.
export function emptyMenuNode(): IvrMenuNode {
  return { message: undefined, error: undefined, options: [] };
}

export function findMenuNode(root: IvrMenuNode, path: number[]): IvrMenuNode {
  let node = root;
  for (const digit of path) {
    const opt = node.options.find((o) => o.option === digit);
    if (!opt || opt.type !== "submenu") {
      throw new Error(`Caminho inválido: a tecla ${digit} não leva a um submenu`);
    }
    node = opt.node;
  }
  return node;
}

export type MenuBreadcrumb = { label: string; path: number[] };

export function buildMenuBreadcrumbs(
  root: IvrMenuNode,
  path: number[]
): MenuBreadcrumb[] {
  const crumbs: MenuBreadcrumb[] = [{ label: "Menu principal", path: [] }];
  let node = root;
  const acc: number[] = [];
  for (const digit of path) {
    const opt = node.options.find((o) => o.option === digit);
    if (!opt || opt.type !== "submenu") break;
    acc.push(digit);
    crumbs.push({ label: `${digit} · ${opt.name}`, path: [...acc] });
    node = opt.node;
  }
  return crumbs;
}

// Próxima tecla livre (0-9) num nível, ou null se as 10 já estão em uso.
export function nextAvailableDigit(options: IvrMenuOption[]): number | null {
  const used = new Set(options.map((o) => o.option));
  for (let d = 1; d <= 9; d++) if (!used.has(d)) return d;
  if (!used.has(0)) return 0;
  return null;
}

// currentDigit: a tecla que a opção já tinha antes da edição, pra não
// acusar colisão dela consigo mesma ao editar sem trocar de tecla.
export function isDigitAvailable(
  options: IvrMenuOption[],
  digit: number,
  currentDigit?: number
): boolean {
  return !options.some((o) => o.option === digit && o.option !== currentDigit);
}

// Todos os queueId referenciados em qualquer ponto da árvore — usado pelo
// seletor "usar fila existente" pra sinalizar filas já em uso em outro nó.
export function collectReferencedQueueIds(node: IvrMenuNode): string[] {
  const ids: string[] = [];
  const walk = (n: IvrMenuNode) => {
    n.options.forEach((o) => {
      if (o.type === "queue") ids.push(o.queueId);
      else walk(o.node);
    });
  };
  walk(node);
  return ids;
}

// Substitui o nó em `path` pelo retorno de `updater(nó atual)`, clonando só
// os nós no caminho (o resto da árvore é reaproveitado). Uso típico:
// setMenu((prev) => updateNodeAtPath(prev || emptyMenuNode(), path, (node) => ({ ...node, options: [...] })))
export function updateNodeAtPath(
  root: IvrMenuNode,
  path: number[],
  updater: (node: IvrMenuNode) => IvrMenuNode
): IvrMenuNode {
  if (path.length === 0) return updater(root);
  const [digit, ...rest] = path;
  return {
    ...root,
    options: root.options.map((o) => {
      if (o.option !== digit || o.type !== "submenu") return o;
      return { ...o, node: updateNodeAtPath(o.node, rest, updater) };
    }),
  };
}

// Remove toda referência a uma fila em qualquer ponto da árvore — usado ao
// excluir a fila em si (não só desvincular uma opção do menu).
export function removeQueueReferences(
  root: IvrMenuNode,
  queueId: string
): IvrMenuNode {
  return {
    ...root,
    options: root.options
      .filter((o) => !(o.type === "queue" && o.queueId === queueId))
      .map((o) =>
        o.type === "submenu"
          ? { ...o, node: removeQueueReferences(o.node, queueId) }
          : o
      ),
  };
}

// Troca queueId referenciados na árvore pelos ids reais do Firestore,
// usado depois que saveQueues resolve os tempId de filas recém-criadas
// nesta mesma sessão de salvamento (ver IvrPageHook.onSave).
export function remapQueueIds(
  root: IvrMenuNode,
  idMap: Record<string, string>
): IvrMenuNode {
  return {
    ...root,
    options: root.options.map((o) => {
      if (o.type === "queue") {
        const mapped = idMap[o.queueId];
        return mapped ? { ...o, queueId: mapped } : o;
      }
      return { ...o, node: remapQueueIds(o.node, idMap) };
    }),
  };
}

// Suficiente checar só as opções da raiz: pra existir um submenu em
// qualquer profundidade, precisa existir um em profundidade 1 primeiro.
// Mesmo critério usado pelo backend/configurador pra decidir se entram no
// caminho recursivo (ver "Compatibilidade" em docs/ura-multinivel-contrato.md).
export function hasAnySubmenu(root: IvrMenuNode): boolean {
  return root.options.some((o) => o.type === "submenu");
}
